/**
 * get old jquery tag path by sort property name (for compatibility with old links)
 * returns tagName
 */
const getSortTagByProperty = (sortProperty: string) => {

    let sortPath = '.default-order'; // mainOrder

    switch(sortProperty){
        case 'skuPrice':{
            sortPath = '.hidden-price';
            break;
        }
        case 'reviewsTotal':{
            sortPath = '.reviews-number';
            break;
        }
        case 'documentName':{
            sortPath = 'p.title%20a';
            break;
        }
    }

    return sortPath;
};

/**
 * get sort property name by old jquery tag path (for compatibility with old links)
 * returns sortProperty
 */
const getSortPropertyByTag = (tagName: string) => {

    let sortProperty = 'mainOrder'; // .default-order

    switch(tagName){
        case '.hidden-price':{
            sortProperty = 'skuPrice';
            break;
        }
        case '.reviews-number':{
            sortProperty = 'reviewsTotal';
            break;
        }
        case 'p.title%20a':
        case 'p.title a':
        case 'p.title+a':{
            sortProperty = 'documentName';
            break;
        }
    }

    return sortProperty;
};

/**
 * Generate deep link
 * Example: https://dev.waves.com/specials#sort:path~type~order=.hidden-price~number~desc|views:view=grid-view|paging:currentPage=0|paging:number=60
 * sortType - text|number|datetime
 * sortOrder - asc|desc
 * layoutMode - grid|list
 */
export const generateDeepLink = (
    sortProperty: string,
    sortType: string,
    sortOrder: string,
    currentPage: number,
    itemsPerPage: number,
    layoutMode: string,
    filterTerm: string) => {

    const parts = [];

    // sort
    const sortPath = getSortTagByProperty(sortProperty);
    parts.push(`sort:path~type~order=${sortPath}~${sortType}~${sortOrder}`);

    // themes (layout)
    parts.push(`views:view=${layoutMode}-view`);

    // pagination
    parts.push(`paging:currentPage=${currentPage}`);
    parts.push(`paging:number=${itemsPerPage}`);

    // filter
    if(filterTerm !== undefined && filterTerm.trim() !== ''){
        parts.push(`filter:term=${filterTerm.trim()}`);
    }

    return parts.join('|');
};

/**
 * update deep link -> replace it in URL
 */
export const updateDeepLink = (deepLink: string, hashStart: string = '#') => {

    let hashStr = deepLink.replace(hashStart, '').trim();

    if(hashStr === ''){
        hashStr = hashStart;
    }
    else{
        hashStr = hashStart + hashStr;
    }

    if(window.location.hash !== hashStr){

        const index = window.location.href.indexOf(hashStart);
        let href;

        if(index === -1){
            href = window.location.href + hashStr;
        }
        else{
            href = window.location.href.substring(0, index) + hashStr;
        }

        if('replaceState' in window.history){
            window.history.replaceState({}, '', href);
        }
        else{
            window.location.replace(href);
        }
    }
};

/**
 * take param1=val1 and return an object of the structure { key: param1, value: val1 }
 * Example: https://dev.waves.com/specials#sort:path~type~order=.hidden-price~number~desc|views:view=grid-view|paging:currentPage=0|paging:number=60
 */
const getParam = (param: string) => {

    if(!param) return null;

    const parts = param.split('=');

    if(parts.length < 2) return null;

    return {
        key: parts[0].trim().toLowerCase(),
        value: parts[1].trim().toLowerCase()
    };
};

/**
 * get deep link parameters from URL
 * hash - window.location.hash value
 * hashStart - optional value of hash start; by default it's '#', but can be changed for example to be '#!'
 * returns params map
 *
 * URL structure example:
 * https://dev.waves.com/specials#sort:path~type~order=.hidden-price~number~desc|views:view=grid-view|paging:currentPage=0|paging:number=60
 */
export const getUrlParams = (hash: string, hashStart = '#') => {

    const paramsMap = new Map();

    if(!hash) return paramsMap;

    const formattedHash = window.decodeURIComponent(hash.replace(hashStart, '')).trim().toLowerCase();

    if(!formattedHash) return paramsMap;

    const params = formattedHash.split('|');

    for(let i=0; i<params.length; i++){
        const param = getParam(params[i]);

        if(!param || !param.key) continue;

        switch(param.key){
            case 'sort:path~type~order': {

                const parts = param.value.split('~');

                if(parts.length === 3) {
                    paramsMap.set('sortProperty', getSortPropertyByTag(parts[0]));

                    // text|number|datetime
                    if(parts[1] === 'text' || parts[1] === 'number') {
                        paramsMap.set('sortType', parts[1]);
                    }

                    if(parts[2] === 'asc' || parts[2] === 'desc') {
                        paramsMap.set('sortOrder', parts[2]);
                    }
                }
                break;
            }

            case 'views:view': {
                paramsMap.set('layoutMode', param.value === 'list-view' ? 'list' : 'grid');
                break;
            }

            case 'paging:currentpage': {
                paramsMap.set('currentPage', Number(param.value) || 0);
                break;
            }

            case 'paging:number': {
                const itemsPerPageValue = Number(param.value);
                paramsMap.set('itemsPerPage', isNaN(itemsPerPageValue) ?  20 : itemsPerPageValue);
                break;
            }

            case 'filter:term': {
                paramsMap.set('filterTerm', param.value !== undefined && param.value.trim() !== '' ? param.value.trim() : '');
                break;
            }
        }
    }

    return paramsMap;
};
